<template>
  <Modal
    hide-close-button
    :modal-style="{
      padding: 0,
      maxHeight: '95vh',
    }"
  >
    <div class="tw:p-3 tw:max-w-[480px] tw:flex tw:flex-col">
      <div class="tw:flex tw:mx-4 tw:text-ds-denim-8 tw:justify-between">
        <div class="tw:text-center">{{ slide + 1 }}/{{ assets.length }}</div>

        <BaseButton
          flat
          icon="close"
          icon-size="20px"
          padding="none"
          @click="closeModal"
        />
      </div>

      <div class="tw:relative tw:w-full tw:max-w-[480px]">
        <div class="tw:absolute tw:left-0 tw:top-1/2 tw:z-10">
          <BaseButton
            class="tw:mr-4"
            color="mid-grey"
            icon="keyboard_arrow_left"
            padding="none"
            @click="decrementSlide"
          />
        </div>

        <div class="tw:mx-auto tw:max-w-[300px]">
          <!-- we set min height here for a square aspect video. We don't know aspect ratio in advance -->
          <video
            v-if="isPlayable(assets[slide])"
            :key="slide"
            class="tw:block tw:w-full tw:h-full tw:min-h-[300px]"
            controls
          >
            <source :src="currentSource" />
          </video>
          <QImg
            v-else
            spinner-color="social"
            :src="currentSource"
          />
        </div>

        <div class="tw:absolute tw:right-0 tw:top-1/2 tw:z-10">
          <BaseButton
            class="tw:ml-4"
            color="mid-grey"
            icon="keyboard_arrow_right"
            padding="none"
            @click="incrementSlide"
          />
        </div>
      </div>

      <div
        v-if="mention"
        class="tw:flex"
      >
        <SocialMentionCardFactory
          content-class="shadow-0"
          hide-border-color
          hide-engagement
          hide-media
          :mention="mention"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import SocialMentionCardFactory from "shared/components/SocialMentionCardFactory";
import { isWebVideoFileUrl } from "shared/helpers/media";
import ModalService from "shared/modals/ModalService";
import { type Nullable } from "shared/types";
import { type Mention } from "shared/types/mentions";

interface Asset {
  thumbnail: string;
  type: string;
  url?: string;
}

interface LightboxSocialPostModalProps {
  assets: Asset[];
  mention?: Nullable<Mention>;
}

const props = withDefaults(defineProps<LightboxSocialPostModalProps>(), {
  mention: null,
});

const slide = ref<number>(0);

const currentSource = computed(() => {
  const asset = props.assets[slide.value];

  if (isPlayable(asset)) {
    return Object.values(asset).find((value) => isWebVideoFileUrl(value));
  }

  // for legacy reasons we need to check both thumbnail and url
  return asset.thumbnail || asset.url;
});

const closeModal = () => {
  ModalService.close("LightboxSocialPostModal");
};

const incrementSlide = () => {
  if (slide.value + 1 > props.assets.length - 1) {
    slide.value = 0;
  } else {
    slide.value += 1;
  }
};

const decrementSlide = () => {
  if (slide.value - 1 < 0) {
    slide.value = props.assets.length - 1;
  } else {
    slide.value -= 1;
  }
};

const isPlayable = (asset: Asset): boolean => {
  const videoExtensions = ["mp4", "webm", "ogg", "mov"];

  const hasVideoExtension = videoExtensions.some((ext) =>
    asset.thumbnail.toLowerCase().endsWith(ext)
  );

  return ["video"].includes(asset.type) || hasVideoExtension;
};
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/fade";
</style>
